import * as ko from "knockout";
import {AbstractDerived} from "./AbstractDerived";
import {IDerivedMutableParams} from "./index";
import {GroupViewModel} from "./group";
import {ItemViewModel} from "./item";
import {nsRuleDerived} from "./nsRuleDerived";
import {MAX_RESULTS_PER_PAGE, ObjectTypeId, REQUEST_THROTTLE} from "../../../constants";
import {ICondition, IObjectData, IObjectType, IRule, IRuleset, TCondition, TSyncfield} from "../../../index";
import {createErrorMessage, getObjectType, getRawSyncFieldValue, getSyncFieldDisplayName} from "../../../util/misc";
import {nsRule} from "../mutable/rule/nsRule";
import {nsDynamicVariable} from "../../dynamic-variable/nsDynamicVariable";
import {PLEASE_WAIT_MESSAGE} from "../../../constants/Language";
import {global, lm} from "../../../appContext";
import {getSearchObjectParams} from "../../../util/request";
import {nsRuleset} from "../nsRuleset";
import {MutableRuleset} from "../mutable/mutableRuleset";
import {MutableRule} from "../mutable/rule/MutableRule";
import {storeObservable} from "../../../util/sessionstore";
import {nsRequest} from "../../../req/fn";
import getPromiseRequest = nsRequest.getPromiseRequest;
import detailsHtml from './details.html?raw';

export const template = detailsHtml;

export class viewModel extends AbstractDerived {
    private readonly list: KnockoutObservableArray<GroupViewModel | ItemViewModel> = ko.observableArray()
    private readonly numItemsDefault: KnockoutObservable<number> = storeObservable('num.ruleset.derived.values')
    private readonly numItems: KnockoutObservable<number> = ko.observable(this.numItemsDefault())
    private readonly objectType: IObjectType;
    private readonly objectTypeId: ObjectTypeId;
    private readonly open: KnockoutObservable<boolean> = ko.observable(false);

    constructor(private params: IDerivedMutableParams) {
        super();
        this.isReady(true)
        this.objectTypeId = this.getRuleset().objecttype.id
        this.objectType = getObjectType(this.objectTypeId)
    }

    private getList = ko.pureComputed((): (GroupViewModel | ItemViewModel)[] => {
        return this._getList().slice(0, this.numItems())
    }).extend({rateLimit: REQUEST_THROTTLE})

    private _getList = ko.pureComputed((): (GroupViewModel | ItemViewModel)[] => {
        const ret = []
        ret.push(...this.getDerivedAttributes())
        ret.push(...this.getDerivedArguments())
        return ret
    })

    private getDerivedAttributes(): (GroupViewModel | ItemViewModel)[] {
        const attr = this.getAttribute()
        const argumentOperatorType = this.getArgumentOperatorObject()?.type;
        if (!attr || !argumentOperatorType) {
            return []
        }
        if (argumentOperatorType == 'array') {
            const arr = nsRule.parseArrayArgument(attr)
            return arr.map(o => this.getDerivedExpression(o)).filter((item): item is GroupViewModel | ItemViewModel => item != null)
        } else {
            const item = this.getDerivedExpression(attr);
            return item ? [item] : []
        }
    }

    private getDerivedArguments(): (GroupViewModel | ItemViewModel)[] {
        const arg = this.getArgument()
        if (!arg) {
            return []
        }
        if (this.getArgumentOperatorObject()?.type == 'array') {
            const arr = nsRule.parseArrayArgument(arg)
            return arr.map(o => this.getDerivedExpression(o) || this.getDerivedArgument(o)).filter((item): item is GroupViewModel | ItemViewModel => item != null)
        } else {
            const item = this.getDerivedExpression(arg) || this.getDerivedArgument(arg);
            return item ? [item] : []
        }
    }

    private getDerivedExpression(value: (IRule['attribute'] | IRule['argument'])): GroupViewModel | ItemViewModel | null {
        const val = String(value)

        /***************************************************************************************************************
         * OBJECTPERMISSION.SYSTEMROLE
         **************************************************************************************************************/
        if (val.startsWith('[OBJECTPERMISSION.SYSTEMROLE;')) {
            const arr = nsDynamicVariable.getArguments(val)
            const id = arr[3]?.str;
            if (!id) {
                return null;
            }
            const item = new ItemViewModel()
            item.title('((has permission)):')
            item.prefix(id)
            item.component = {
                name: 'component/variable/system-role-by-id',
                params: {
                    id: id,
                },
            }
            return item
        }

        /***************************************************************************************************************
         * OBJECTPERMISSION.SYSTEM
         **************************************************************************************************************/
        if (val.startsWith('[OBJECTPERMISSION.SYSTEM;')) {
            const arr = nsDynamicVariable.getArguments(val)
            const id = arr[3]?.str;
            if (!id) {
                return null;
            }
            const item = new ItemViewModel()
            item.title('((has permission in collection)):')
            item.prefix(id)
            item.component = {
                name: 'component/variable/system-by-id',
                params: {id: id, showIcon: false, linkable: true},
            }
            return item
        }

        /***************************************************************************************************************
         * DESCENDANTS
         **************************************************************************************************************/
        if (val.startsWith('[DESCENDANTS;')) {
            const arr = nsDynamicVariable.getArguments(val)
            const title = arr[0]?.str;
            const srcId = arr[1]?.str;
            const prefixKey = arr[2]?.str;
            if (!title || !srcId || !prefixKey) {
                return null;
            }
            const group = new GroupViewModel()
            group.title(title)
            group.setPrefix(srcId)
            group.messageData({message: PLEASE_WAIT_MESSAGE})
            group.isReady(false)
            let ids: number[] = []
            group.req = global.getPromiseRequest('searchobject')
            group.req
                // first retrieve all the eAdm ids for the expression
                // (using searchobject rather than evaluate expression to make this functionality available for non company administrators)
                .request(getSearchObjectParams({
                    onlyids: true,
                    resultsprpage: MAX_RESULTS_PER_PAGE,
                    objecttypeid: ObjectTypeId.Department,
                    jruleset: JSON.stringify(nsRuleset.generateRuleset({
                        objecttype: getObjectType(ObjectTypeId.Department),
                        rules: [nsRuleset.generateRule({
                            attribute: prefixKey,
                            argumentoperator: 'Er lik en av',
                            argument: val
                        })]
                    }))
                }))
                // having all the ids we can map those to item components
                .then((_ids: number[]) => {
                    ids = _ids
                })
                .then(() => {
                    return getPromiseRequest('searchobject')
                        .request(getSearchObjectParams({
                            objecttypeid: ObjectTypeId.Department,
                            jruleset: JSON.stringify(nsRuleset.generateRuleset({
                                objecttype: getObjectType(ObjectTypeId.Department),
                                rules: [nsRuleset.generateRule({
                                    attribute: 'Id',
                                    argumentoperator: 'Er lik',
                                    argument: parseInt(srcId),
                                })]
                            }))
                        }))
                        .then((data: [IObjectData]) => {
                            let name: string
                            if (!data.length) {
                                name = '((unknown))'
                            } else {
                                const first = data[0];
                                name = first ? getSyncFieldDisplayName(ObjectTypeId.Department, first.syncfields) : '((unknown))';
                            }
                            const title = `${name} ((with subdepartments|ac))`
                            group.title(title)
                        })
                })
                .then(() => {
                    if (!ids.length) {
                        group.messageData({message: 'no data'})
                        return
                    }
                    ids.forEach(id => {
                        if (id.toString() != val) {
                            const item = new ItemViewModel()
                            item.component = {
                                name: "component/variable/object-by-any",
                                params: {
                                    argument: id,
                                    argumentoperator: 'Er lik',
                                    attribute: 'Id',
                                    objectTypeId: ObjectTypeId.Department,
                                    onLoad: (o: IObjectData) => {
                                        const prefix = getRawSyncFieldValue(prefixKey as TSyncfield, o.syncfields);
                                        item.prefix(prefix ?? null)
                                    },
                                    syncfieldNames: 'OrgUnitnr',
                                },
                            };
                            if (id.toString() == srcId) {
                                group.items.unshift(item)
                            } else {
                                group.items.push(item)
                            }
                        }
                    })
                    group.messageData(null)
                    group.isReady(true)
                })
                .catch((reason: string) => {
                    group.messageData(createErrorMessage(String(reason)))
                })
            return group
        }
        return null
    }

    private getDerivedArgument(value: IRule['argument']): GroupViewModel | ItemViewModel | null {
        const val = String(value)
        const attr = this.getAttribute();
        const attribute = nsDynamicVariable.getArguments(attr)?.[0]?.str ?? attr;

        /***************************************************************************************************************
         * EXACT OPERATOR
         **************************************************************************************************************/
        if (this.isOperatorExact()) {
            const item = new ItemViewModel()
            item.prefix(val)
            switch (attribute) {
                // -----------------------------------------------------------------------------------------------------
                // Message flow
                // -----------------------------------------------------------------------------------------------------
                case "ApplicationMessageId":
                case "RejectionMessageId": {
                    item.component = {
                        name: "component/variable/messagetemplate",
                        params: {id: val, linkable: true, target: '_blank'},
                    };
                    return item
                }
                // -----------------------------------------------------------------------------------------------------
                // Ruleset
                // -----------------------------------------------------------------------------------------------------
                case 'AvailableToRuleSetId':
                case 'ManagedByRuleSetId':
                case 'MemberRuleSetId': {
                    item.component = {
                        name: "component/variable/ruleset",
                        params: {id: val, linkable: true, target: '_blank'},
                    };
                    return item
                }
                // -----------------------------------------------------------------------------------------------------
                // Department
                // -----------------------------------------------------------------------------------------------------
                case 'AUnitId':
                case "DepartmentNumber":
                case 'ParentDepartmentNumber': {
                    const attribute = this.objectTypeId == ObjectTypeId.User
                        ? 'OrgUnitnr' // map User.DepartmentNumber to Department.OrgUnitnr
                        : 'DepartmentNumber' // otherwise it's always mapped to Department.DepartmentNumber
                    item.component = {
                        name: "component/variable/object-by-any",
                        params: {
                            argument: val,
                            argumentoperator: 'Er lik',
                            attribute: attribute,
                            objectTypeId: ObjectTypeId.Department,
                        },
                    };
                    return item
                }
                // -----------------------------------------------------------------------------------------------------
                // Manager
                // -----------------------------------------------------------------------------------------------------
                case "APositionManager":
                case "Manager":
                case "PermanantManager": {
                    let rules: IRule[] = nsRuleset.getRulesForFetchingAManager(val);
                    item.component = {
                        name: "component/variable/object-by-any",
                        params: {
                            objectTypeId: ObjectTypeId.User,
                            rules: rules,
                        },
                    };
                    return item
                }
                // -----------------------------------------------------------------------------------------------------
                // ManagerOf
                // -----------------------------------------------------------------------------------------------------
                case "ManagerOf": {
                    item.component = {
                        name: "component/variable/object-by-any",
                        params: {
                            argument: val,
                            argumentOperator: 'Er lik',
                            attribute: 'SourceId',
                            objectTypeId: ObjectTypeId.Department,
                        },
                    };
                    return item
                }
                // -----------------------------------------------------------------------------------------------------
                // SourceId
                // -----------------------------------------------------------------------------------------------------
                case "Parent":
                case "SourceId": {
                    if (this.objectTypeId == ObjectTypeId.Group) {
                        if (val === "0" || val === "Root") {
                            return null;
                        }
                        item.component = {
                            name: "component/variable/object-by-any",
                            params: {
                                objectTypeId: ObjectTypeId.Group,
                                argument: val,
                                attribute: 'SourceId',
                            },
                        }
                        return item
                    }
                    break
                }
                // -----------------------------------------------------------------------------------------------------
                // EmployeeStatusCode
                // -----------------------------------------------------------------------------------------------------
                case 'EmployeeStatusCode': {
                    if (val?.length == 7) {
                        item.component = {
                            name: 'component/variable/employee-status-code',
                            params: {code: val},
                        }
                        return item
                    } else {
                        item.messageData({message: 'invalid argument', type: 'warning'})
                    }
                    return item
                }
            }
        }

        /***************************************************************************************************************
         * FUZZY OPERATOR
         **************************************************************************************************************/
        if (this.isOperatorFuzzy()) {
            switch (attribute) {
                // -----------------------------------------------------------------------------------------------------
                // Department
                // -----------------------------------------------------------------------------------------------------
                case 'AUnitId':
                case "DepartmentNumber":
                case 'ParentDepartmentNumber': {
                    const attr = this.objectTypeId == ObjectTypeId.User
                        ? 'OrgUnitnr' // map User.DepartmentNumber to Department.OrgUnitnr
                        : 'DepartmentNumber' // otherwise it's always mapped to Department.DepartmentNumber
                    const group = new GroupViewModel()
                    group.title([attribute, lm.t(`condition.${this.getArgumentOperator()}|ac`), val].join (' '))
                    group.messageData({message: PLEASE_WAIT_MESSAGE})
                    group.isReady(false)
                    group.req = global.getPromiseRequest('searchobject')
                    group.req
                        .request(getSearchObjectParams({
                            onlyids: true,
                            resultsprpage: MAX_RESULTS_PER_PAGE,
                            objecttypeid: ObjectTypeId.Department,
                            jruleset: JSON.stringify(nsRuleset.generateRuleset({
                                objecttype: getObjectType(ObjectTypeId.Department),
                                rules: [nsRuleset.generateRule({
                                    attribute: attr,
                                    argumentoperator: this.getArgumentOperator(),
                                    argument: val
                                })]
                            }))
                        }))
                        // having all the ids we can map those to item components
                        .then((ids: number[]) => {
                            if (!ids.length) {
                                group.messageData({message: 'no data'})
                                return
                            }
                            ids.forEach(id => {
                                const item = new ItemViewModel()
                                item.component = {
                                    name: "component/variable/object-by-any",
                                    params: {
                                        argument: id,
                                        argumentoperator: 'Er lik',
                                        attribute: 'Id',
                                        objectTypeId: ObjectTypeId.Department,
                                        onLoad: (o: IObjectData) => {
                                            let prefix = getRawSyncFieldValue('OrgUnitnr', o.syncfields);
                                            item.prefix(prefix ?? '')
                                        },
                                        syncfieldNames: 'OrgUnitnr',
                                    },
                                };
                                group.items.push(item)
                            })
                            group.messageData(null)
                            group.isReady(true)
                        })
                        .catch((reason: string) => {
                            group.messageData(createErrorMessage(String(reason)))
                        })
                    return group
                }
            }
        }
        return null
    }

    private getRuleset = ko.pureComputed((): IRuleset => {
        const ruleset = this.params.ruleset;
        if (ruleset instanceof MutableRuleset) {
            return ruleset.getSubmitDataWithRules()
        }
        return ruleset
    })

    private getRule = ko.pureComputed((): IRule => {
        const rule = this.params.rule;
        if (rule.constructor == MutableRule) {
            return rule.toStatic()
        } else {
            return rule as IRule
        }
    })

    private getAttribute() {
        return this.getRule().attribute
    }

    private getArgument(): IRule['argument'] {
        return this.getRule().argument
    }

    private getArgumentOperatorObject(): ICondition | undefined {
        const name = this.getArgumentOperator()
        return this.params.conditions.find(o => o.name == name)
    }

    private getArgumentOperator(): TCondition {
        return this.getRule().argumentoperator
    }

    private isOperatorExact(): boolean {
        return nsRuleDerived.ExactOperators.includes(this.getArgumentOperator())
    }

    private isOperatorFuzzy(): boolean {
        return nsRuleDerived.FuzzyOperators.includes(this.getArgumentOperator())
    }
}
